import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useChat } from '../contexts/ChatContext';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { db } from '../services/firebase';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom'; // Import useNavigate

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  height: 100vh;
  display: flex;
  flex-direction: column;
`;

const ChatContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #f5f5f5;
  border-radius: 10px;
  overflow: hidden;
  margin-top: 20px;
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const MessageBubble = styled.div`
  max-width: 70%;
  padding: 12px 16px;
  border-radius: 18px;
  word-wrap: break-word;
  align-self: ${props => props.isOwn ? 'flex-end' : 'flex-start'};
  background: ${props => {
    if (props.isAI) return '#e3f2fd';
    if (props.isOwn) return '#007bff';
    return '#ffffff';
  }};
  color: ${props => props.isOwn ? 'white' : '#333'};
  border: ${props => props.isAI ? '2px solid #2196f3' : 'none'};
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
`;

const MessageInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
  font-size: 12px;
  color: #666;
`;

const Avatar = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
`;

const InputContainer = styled.div`
  padding: 20px;
  background: white;
  border-top: 1px solid #e0e0e0;
  display: flex;
  gap: 10px;
  align-items: center;
`;

const MessageInput = styled.input`
  flex: 1;
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 25px;
  outline: none;
  font-size: 14px;
  
  &:focus {
    border-color: #007bff;
  }
`;

const SendButton = styled.button`
  padding: 12px 20px;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 25px;
  cursor: pointer;
  font-weight: 500;
  transition: background 0.2s;
  
  &:hover {
    background: #0056b3;
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const AIButton = styled.button`
  padding: 8px 16px;
  background: #2196f3;
  color: white;
  border: none;
  border-radius: 20px;
  cursor: pointer;
  font-size: 12px;
  transition: background 0.2s;
  
  &:hover {
    background: #1976d2;
  }
`;

const AdminControls = styled.div`
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 10px;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 20px;
  color: #666;
`;

const ErrorMessage = styled.div`
  background: #f8d7da;
  color: #721c24;
  padding: 10px;
  border-radius: 5px;
  margin-bottom: 10px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px; /* Added padding */
  background-color: #ffffff; /* Added background color */
  border-bottom: 1px solid #e0e0e0; /* Added border */
  border-top-left-radius: 10px; /* Match container border radius */
  border-top-right-radius: 10px; /* Match container border radius */
`;

const ProfileLink = styled(Link)`
  text-decoration: none;
  color: #007bff;
  font-weight: bold;
  padding: 8px 15px;
  border: 1px solid #007bff;
  border-radius: 20px;
  transition: background-color 0.2s, color 0.2s;
  display: flex; /* Added to align icon and text */
  align-items: center; /* Added to align icon and text */
  gap: 5px; /* Added space between icon and text */

  &:hover {
    background-color: #007bff;
    color: white;
    text-decoration: none;
  }
`;

const LogoutButton = styled.button`
  padding: 8px 15px;
  background: none;
  color: #dc3545;
  border: 1px solid #dc3545;
  border-radius: 20px;
  cursor: pointer;
  font-weight: bold;
  transition: background-color 0.2s, color 0.2s;
  display: flex; /* Added to align icon and text */
  align-items: center; /* Added to align icon and text */
  gap: 5px; /* Added space between icon and text */

  &:hover {
    background-color: #dc3545;
    color: white;
  }
`;

const UserListContainer = styled.div`
  background: #e9ecef;
  border: 1px solid #dee2e6;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 10px;
`;

const UserListItem = styled.div`
  padding: 12px;
  cursor: pointer;
  border-bottom: 1px solid #ced4da;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background-color 0.2s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #cfd8dc;
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const UserName = styled.div`
  font-weight: ${props => props.hasUnread ? 'bold' : 'normal'};
  color: ${props => props.hasUnread ? '#007bff' : '#333'};
`;

const UserEmail = styled.div`
  font-size: 12px;
  color: #666;
`;

const UnreadBadge = styled.div`
  background: #dc3545;
  color: white;
  border-radius: 50%;
  min-width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  margin-left: 10px;
`;

const BackButton = styled.button`
  padding: 8px 16px;
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 20px;
  cursor: pointer;
  font-size: 12px;
  transition: background 0.2s;
  margin-right: 10px;
  
  &:hover {
    background: #5a6268;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 10px; /* Space between buttons */
  align-items: center;
`;

export default function ChatPage() {
  const { currentUser, userRole, getAllUsers, logout } = useAuth(); // Added logout here
  const { messages, loading, error, sendMessage, sendAIMessage, currentRoomId, setChatRoom, createRoomId, hasActiveRoom } = useChat();
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [lastReadTimestamps, setLastReadTimestamps] = useState({});
  const messagesEndRef = useRef(null);
  const navigate = useNavigate(); // Initialize useNavigate

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Fetch all users if the current user is super_admin
  useEffect(() => {
    if (userRole === 'super_admin') {
      const fetchUsers = async () => {
        const users = await getAllUsers();
        // Filter out the current super admin user from the list
        const filteredUsers = users.filter(user => user.uid !== currentUser?.uid);
        setAllUsers(filteredUsers);
        
        // Initialize last read timestamps for each user (you might want to store this in localStorage or Firebase)
        const initialTimestamps = {};
        filteredUsers.forEach(user => {
          const storedTimestamp = localStorage.getItem(`lastRead_${currentUser?.email}_${user.email}`);
          initialTimestamps[user.email] = storedTimestamp ? new Date(storedTimestamp) : new Date(0);
        });
        setLastReadTimestamps(initialTimestamps);
      };
      fetchUsers();
    }
  }, [userRole, currentUser, getAllUsers]);

  // Listen for unread messages for each user (super admin only)
  useEffect(() => {
    if (userRole !== 'super_admin' || !currentUser?.email || allUsers.length === 0) return;

    const unsubscribeFunctions = [];

    allUsers.forEach(user => {
      const roomId = createRoomId 
        ? createRoomId(currentUser.email, user.email)
        : `${currentUser.email}_${user.email}`;
      
      const messagesRef = collection(db, `directMessages/${roomId}/messages`);
      const messagesQuery = query(
        messagesRef, 
        where('uid', '!=', currentUser.uid),
        orderBy('uid'),
        orderBy('createdAt', 'desc')
      );

      const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
        const lastReadTime = lastReadTimestamps[user.email] || new Date(0);
        let unreadCount = 0;

        snapshot.docs.forEach(doc => {
          const message = doc.data();
          const messageTime = message.createdAt?.toDate() || new Date();
          
          if (messageTime > lastReadTime && message.uid !== currentUser.uid) {
            unreadCount++;
          }
        });

        setUnreadCounts(prev => ({
          ...prev,
          [user.email]: unreadCount
        }));
      }, (error) => {
        console.error(`Error listening to messages for ${user.email}:`, error);
      });

      unsubscribeFunctions.push(unsubscribe);
    });

    return () => {
      unsubscribeFunctions.forEach(unsubscribe => unsubscribe());
    };
  }, [userRole, currentUser, allUsers, lastReadTimestamps, createRoomId]);

  // Helper function to create room ID (fallback if not available from context)
  const createRoomIdLocal = (email1, email2) => {
    return `${email1}_${email2}`;
  };

  const handleLogout = async () => {
    try {
      await logout(); // Corrected back to calling logout directly
      // Redirect to login page or home page after logout
      navigate('/'); // Redirect to the landing 
    } catch (error) {
      console.error('Failed to log out:', error);
      // Optionally show an error message to the user
    }
  };

  // Effect to set the chat room when a user is selected or for regular users
  useEffect(() => {
    if (!currentUser?.email) return;

    if (userRole === 'super_admin') {
      // Super admin: only set room when a user is selected
      if (selectedUser && selectedUser.email) {
        const roomId = createRoomId 
          ? createRoomId(currentUser.email, selectedUser.email)
          : createRoomIdLocal(currentUser.email, selectedUser.email);
        setChatRoom(roomId);
      } else {
        // No user selected, clear the room
        setChatRoom(null);
      }
    } else {
      // Regular users: always in direct chat with super admin
      const superAdminEmail = process.env.REACT_APP_SUPER_ADMIN_EMAIL;
      if (superAdminEmail) {
        const roomId = createRoomId 
          ? createRoomId(superAdminEmail, currentUser.email)
          : createRoomIdLocal(superAdminEmail, currentUser.email);
        console.log('Setting up room for regular user:', roomId); // Debug log
        setChatRoom(roomId);
      } else {
        console.error("Super admin email not found in environment variables.");
        console.error("Please add REACT_APP_SUPER_ADMIN_EMAIL to your .env file");
      }
    }
  }, [selectedUser, userRole, currentUser, setChatRoom, createRoomId]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageText.trim() || sending || !hasActiveRoom) return;

    setSending(true);
    try {
      await sendMessage(messageText.trim());
      setMessageText('');
    } catch (err) {
      console.error('Error sending message:', err);
    } finally {
      setSending(false);
    }
  };

  const handleSendAIMessage = async () => {
    if (userRole !== 'super_admin' || !hasActiveRoom) return;

    const aiResponses = [
      "Hello! I'm your AI assistant. How can I help you today?",
      "I'm here to assist with any questions you might have.",
      "Feel free to ask me anything!",
      "Thanks for using our chat service!"
    ];

    const randomResponse = aiResponses[Math.floor(Math.random() * aiResponses.length)];
    await sendAIMessage(randomResponse);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    
    // Mark messages as read when selecting a user
    if (currentUser?.email && user.email) {
      const now = new Date().toISOString();
      localStorage.setItem(`lastRead_${currentUser.email}_${user.email}`, now);
      
      // Update last read timestamp and clear unread count
      setLastReadTimestamps(prev => ({
        ...prev,
        [user.email]: new Date(now)
      }));
      
      setUnreadCounts(prev => ({
        ...prev,
        [user.email]: 0
      }));
    }
  };

  const handleBackToUserList = () => {
    setSelectedUser(null);
  };

  if (loading) {
    return (
      <Container>
        <LoadingMessage>Loading chat...</LoadingMessage>
      </Container>
    );
  }

  // Show user selection for super admin when no user is selected
  if (userRole === 'super_admin' && !selectedUser) {
    return (
      <Container>
        <Header>
          <h2>Select User to Chat</h2>
          <ButtonContainer>
            <ProfileLink to="/profile">{/* Profile Icon */} Profile Settings</ProfileLink>
            <LogoutButton onClick={handleLogout}>{/* Logout Icon */} Logout</LogoutButton>
          </ButtonContainer>
        </Header>
        
        <UserListContainer>
          <h3>Available Users</h3>
          {allUsers.length > 0 ? (
            allUsers.map(user => {
              const unreadCount = unreadCounts[user.email] || 0;
              const hasUnread = unreadCount > 0;
              
              return (
                <UserListItem key={user.id} onClick={() => handleUserSelect(user)}>
                  <UserInfo>
                    <UserName hasUnread={hasUnread}>
                      {user.displayName || 'Anonymous User'}
                    </UserName>
                    <UserEmail>{user.email}</UserEmail>
                  </UserInfo>
                  {hasUnread && (
                    <UnreadBadge>
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </UnreadBadge>
                  )}
                </UserListItem>
              );
            })
          ) : (
            <LoadingMessage>No users available</LoadingMessage>
          )}
        </UserListContainer>
      </Container>
    );
  }

  // Main chat interface
  return (
    <Container>
      <Header>
        <h2>
          {userRole === 'super_admin' && selectedUser
            ? `Chat with ${selectedUser.displayName}`
            : 'Direct Chat'}
        </h2>
        <ButtonContainer>
          <ProfileLink to="/profile">{/* Profile Icon */} Profile Settings</ProfileLink>
          <LogoutButton onClick={handleLogout}>{/* Logout Icon */} Logout</LogoutButton>
        </ButtonContainer>
      </Header>

      {userRole === 'super_admin' && (
        <AdminControls>
          <h3>Super Admin Controls</h3>
          {selectedUser && (
            <BackButton onClick={handleBackToUserList}>
              ← Back to User List
            </BackButton>
          )}
          {hasActiveRoom && (
            <AIButton onClick={handleSendAIMessage}>
              Send AI Message
            </AIButton>
          )}
        </AdminControls>
      )}

      {hasActiveRoom ? (
        <ChatContainer>
          {error && (
            <ErrorMessage>
              Error: {error.message || error}
            </ErrorMessage>
          )}

          <MessagesContainer>
            {messages && messages.length > 0 ? (
              messages.map((message, index) => {
                const isOwn = message.uid === currentUser?.uid;
                const isAI = message.isAI || message.uid === 'ai-assistant';

                return (
                  <div key={message.id || index}>
                    {!isOwn && (
                      <MessageInfo>
                        {message.photoURL && (
                          <Avatar src={message.photoURL} alt="Avatar" />
                        )}
                        <span>{message.displayName}</span>
                        <span>{formatTimestamp(message.createdAt)}</span>
                      </MessageInfo>
                    )}
                    <MessageBubble isOwn={isOwn} isAI={isAI}>
                      {message.text}
                    </MessageBubble>
                  </div>
                );
              })
            ) : (
              <LoadingMessage>
                {userRole === 'super_admin' && selectedUser 
                  ? `Start a conversation with ${selectedUser.displayName}!`
                  : 'Start your conversation!'}
              </LoadingMessage>
            )}
            <div ref={messagesEndRef} />
          </MessagesContainer>

          <InputContainer>
            <form onSubmit={handleSendMessage} style={{ display: 'flex', flex: 1, gap: '10px' }}>
              <MessageInput
                type="text"
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                placeholder={
                  userRole === 'super_admin' && selectedUser 
                    ? `Message ${selectedUser.displayName}...` 
                    : "Type your message..."
                }
                disabled={sending || !hasActiveRoom}
              />
              <SendButton 
                type="submit" 
                disabled={sending || !messageText.trim() || !hasActiveRoom}
              >
                {sending ? 'Sending...' : 'Send'}
              </SendButton>
            </form>
          </InputContainer>
        </ChatContainer>
      ) : (
        <LoadingMessage>
          {userRole === 'super_admin' 
            ? 'Select a user to start chatting' 
            : (
              <div>
                <div>Setting up your chat...</div>
                <div style={{ fontSize: '12px', color: '#999', marginTop: '10px' }}>
                  Current User: {currentUser?.email || 'Not logged in'}<br/>
                  User Role: {userRole || 'Not set'}<br/>
                  Super Admin Email: {process.env.REACT_APP_SUPER_ADMIN_EMAIL || 'Not configured'}<br/>
                  Room ID: {currentRoomId || 'Not set'}
                </div>
              </div>
            )}
        </LoadingMessage>
      )}
    </Container>
  );
}

